import { ObjectType } from '@quromedical/fhir-common';
import { AllergyCode, AllergySeverity } from '@quromedical/models';
import { CrudForm } from 'components/form';
import { Field, SelectOption } from 'components/types';
import { TextVariant } from 'design-system';
import { RadioOption } from 'design-system/components';
import { useAllergyOptions } from 'hooks/useAllergies';
import React, { useCallback, useState } from 'react';
import { strings } from 'strings';
import * as yup from 'yup';

export interface AllergyCreateProps {
  onAdd: () => void;
  onChange: (data: Partial<AllergyCode>, hasErrors: boolean) => void;
  onRemove: () => void;
  canAdd: boolean;
  canRemove: boolean;
  showErrors: boolean;
  initialValues: Partial<AllergyCode>;
  title?: string;
  titleVariant?: TextVariant;
}

interface AllergyFields {
  allergy: string;
  severity: string;
}

export const severityOptions: RadioOption<AllergySeverity>[] = [
  {
    display: strings.AllergyMild,
    value: 'mild',
  },
  {
    display: strings.AllergySevere,
    value: 'severe',
  },
  {
    display: strings.AllergyUnknown,
    value: 'unknown',
  },
];

const createFieldsAllergy = (allergyFetcher: SelectOption[]): Field<AllergyFields>[] => [
  {
    subfields: [
      {
        key: 'allergy',
        type: 'combobox-single',
        label: strings.FormLabelAllergies,
        fetcher: allergyFetcher,
        searchable: true,
      },
      {
        key: 'severity',
        type: 'radio-group',
        label: strings.FormLabelSeverity,
        options: severityOptions,
      },
    ],
  },
];

export const schema = yup
  .object<ObjectType<AllergyFields>>({
    allergy: yup.string().required(),
    severity: yup.string().required(),
  })
  .required();

export const AllergyCreateForm: React.FC<AllergyCreateProps> = ({
  onAdd,
  onChange,
  onRemove,
  canAdd,
  canRemove,
  title,
  showErrors,
  initialValues,
  titleVariant,
}) => {
  const { options: allergyOptions } = useAllergyOptions();
  const [allergyDisplay, setAllergyDisplay] = useState(title);

  const handleChange = useCallback(
    (data: Partial<AllergyFields>) => {
      // manually check schema since the internal form does not validate if not touched
      const isValid = schema.isValidSync(data);
      const allergyName = data.allergy?.split(':')[1];
      setAllergyDisplay(allergyName || title);
      onChange(
        {
          code: data.allergy?.split(':')[0],
          name: allergyName,
          severity: data.severity as AllergySeverity,
        },
        !isValid
      );
    },
    [onChange, title]
  );

  const onSubmit = useCallback(() => {
    onAdd();
    return {};
  }, [onAdd]);

  return (
    <CrudForm<AllergyFields>
      title={allergyDisplay}
      hasCard={false}
      fields={createFieldsAllergy(allergyOptions)}
      initialValues={initialValues}
      onSubmit={onSubmit}
      onSecondarySubmit={onRemove}
      showSecondarySubmitButton={canRemove}
      showSubmitButton={canAdd}
      onChange={handleChange}
      showErrors={showErrors}
      validateOnMount
      buttonText={strings.FormListAddItem}
      secondaryButtonText={strings.FormListRemoveItem}
      titleVariant={titleVariant}
      validationSchema={schema}
      onChangeDebounceTime={0}
      validateOnChange
    />
  );
};
